import React from 'react';
import PropTypes from 'prop-types';
import { forOwn, chunk } from 'lodash-es';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import Collapse from '@material-ui/core/Collapse';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { getString } from '../../../../../helpers/stringHelpers.js';
import Grid from '@material-ui/core/Grid';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';
import FlashIcon from '@material-ui/icons/FlashOnOutlined';
import Divider from '@material-ui/core/Divider';
import IndentedCardHeader from '../../../../../components/IndentedCardHeader/IndentedCardHeader.js';
import IndentedCardContent from '../../../../../components/IndentedCardContent/IndentedCardContent.js';
import BigIconFieldReadOnly from '../../../../../components/BigIconFieldReadOnly/BigIconFieldReadOnly';
import ActionButton from '../../../../../components/ActionButton/ActionButton';

import styles from './styles.js';

class JobSummaryCard extends React.Component {
  state = {
    expanded: false,
  };

  constructor(props) {
    super(props);

    this.onToggle = this.onToggle.bind(this);
    this.makeFields = this.makeFields.bind(this);
    this.makeBody = this.makeBody.bind(this);
  }

  onToggle() {
    this.setState({ expanded: !this.state.expanded });
  }

  makeRow(partA) {
    const ret = [
      <React.Fragment key={partA.label}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <BigIconFieldReadOnly
            label={partA.label}
            value={partA.value}
            valueSize='xs'
            icon={partA.icon}
            classes={partA.classes}
          />
        </Grid>
      </React.Fragment>,
    ];

    return ret;
  }

  makeBody(fields, issueDetails) {
    const { classes } = this.props;
    const { expanded } = this.state;

    const body = (
      <React.Fragment>
        <div className={classes.negativeTopMargin}>
          <div className={expanded ? classes.issueDetails : ''}>
            {issueDetails}
          </div>
          <Collapse in={expanded}>
            <Grid container spacing={16} className={classes.collapsedGrid}>
              {chunk(fields, 1).map((item, index) => this.makeRow(item[0]))}
              <Grid item xs={12} className={classes.detailsDivider}>
                <Divider />
              </Grid>
            </Grid>
          </Collapse>
        </div>
      </React.Fragment>
    );

    return body;
  }

  makeFields() {
    const {
      classes,
      data: { comments, causedBy },
    } = this.props;

    const fields = [];

    fields.push({
      label: 'Caused By',
      value: causedBy,
      icon: FlashIcon,
      classes: {
        icon: classes.turquoise,
      },
    });
    if (comments) {
      fields.push({
        label: 'Comments',
        value: comments,
        icon: DescriptionIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    return fields;
  }

  makeSubtitle(subtitle, subtitleTruncated) {
    const { classes } = this.props;
    return (
      <div className={classes.hotspot} onClick={this.onToggle}>
        <Typography className={classes.subtitle}>
          {this.state.expanded ? subtitle : subtitleTruncated}
        </Typography>
      </div>
    );
  }

  render() {
    const {
      classes,
      icon,
      secondaryAction,
      data: { jobs },
      jobNumber,
    } = this.props;

    const { expanded } = this.state;

    const stringsComponent = 'workRequests';

    // Generate subtitle
    let subtitle = [];
    forOwn(jobs, (item, key) =>
      subtitle.push(
        <React.Fragment key={key}>
          <span className={classes.bold}>{key}:</span> {item.join(' | ')}
          <br />
        </React.Fragment>
      )
    );

    const title = (
      <Typography variant='h3' className={classes.cardTitle}>
        {getString(stringsComponent, 'ISSUE_TITLE')}
        {jobNumber ? ` ${jobNumber}` : null}
      </Typography>
    );
    const issueDetails = (
      <React.Fragment>{this.makeSubtitle(subtitle, subtitle)}</React.Fragment>
    );

    const ActionIcon = expanded ? ExpandLessIcon : ExpandMoreIcon;

    return (
      <div className={classes.root}>
        <Card className='Card'>
          <IndentedCardHeader
            title={title}
            avatar={icon}
            action={
              <React.Fragment>
                {secondaryAction && (
                  <div className={classes.buttonContainerTop}>
                    <ActionButton
                      action={secondaryAction}
                      icon={DeleteIcon}
                      label='Remove'
                      classes={{ icon: classes.removeIcon }}
                    />
                  </div>
                )}
                <div
                  className={
                    secondaryAction ? classes.buttonContainerBottom : null
                  }
                >
                  <ActionButton
                    action={this.onToggle}
                    icon={ActionIcon}
                    label={expanded ? 'Collapse' : 'Expand'}
                    small={true}
                  />
                </div>
              </React.Fragment>
            }
            actionHeader={this.onToggle}
            classes={{
              title: classes.title,
              avatar: classes.icon,
            }}
          />
          <IndentedCardContent
            body={this.makeBody(this.makeFields(), issueDetails)}
            noIndent={true}
          />
        </Card>
      </div>
    );
  }
}

JobSummaryCard.propTypes = {
  classes: PropTypes.object.isRequired,
  icon: PropTypes.object.isRequired,
  data: PropTypes.shape({
    comments: PropTypes.string,
    causedBy: PropTypes.string,
    jobs: PropTypes.objectOf(PropTypes.array),
  }),
  secondaryAction: PropTypes.func,
  jobNumber: PropTypes.string,
};

JobSummaryCard.defaultProps = {
  data: {
    comments: null,
    causedBy: '',
    jobs: {},
  },
  secondaryAction: null,
  jobNumber: null,
};

export default withStyles(styles)(JobSummaryCard);
